import {
    type ComponentProps,
    Fragment,
    type ReactElement,
    type ReactNode,
    Suspense,
    use,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useDeferredRender } from '@/hooks/use-deferred-render';
import type { ShopperSearch } from '@/scapi';
import DynamicImageProvider from '@/providers/dynamic-image';
import { useBasket, useBasketLineReadiness } from '@/providers/basket';
import { ProductTileProvider } from '@/components/product-tile';
import { ProductTileCustom } from '@/components/product-tile-custom';
import { ProductTileCustomSkeleton } from '@/components/product-tile-custom/skeleton';
import type { ProductTileCustomLayout } from '@/components/product-tile-custom/product-tile-custom-types';
import { cn } from '@/lib/utils';
import type { DynamicImageDimensions } from '@/lib/images/dynamic-image';
import {
    PRODUCT_CARD_HORIZONTAL_HEIGHTS,
    PRODUCT_CARD_HORIZONTAL_WIDTHS,
    PRODUCT_GRID_EVENT_WIDTHS_WITHOUT_REFINEMENTS,
    PRODUCT_GRID_EVENT_WIDTHS_WITH_REFINEMENTS,
    PRODUCT_GRID_VERTICAL_WIDTHS_WITHOUT_REFINEMENTS,
    PRODUCT_GRID_VERTICAL_WIDTHS_WITH_REFINEMENTS,
} from '@/lib/images/product-card-sizes';

type ProductSearchHit = ShopperSearch.schemas['ProductSearchHit'];

type ProductGridImageDimensions = {
    widths: DynamicImageDimensions;
    heights?: DynamicImageDimensions;
};

function resolveTileBasketControlsDisabled(
    basketHydrationDisabled: boolean,
    product: ProductSearchHit,
    isAddToCartDisabled?: (product: ProductSearchHit) => boolean
): boolean {
    return basketHydrationDisabled || (isAddToCartDisabled?.(product) ?? false);
}

function resolveGridClassName(tileLayout: ProductTileCustomLayout, mobileGridColumns: 1 | 2): string {
    if (tileLayout === 'vertical-event' && mobileGridColumns === 2) {
        return 'grid-cols-2 md:grid-cols-2 lg:grid-cols-4';
    }
    if (tileLayout === 'vertical') {
        return 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3';
    }
    return 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3';
}

/** Row/column gaps for the product CSS grid. Events/courses use larger row gaps. */
function resolveGridGapClassName(tileLayout: ProductTileCustomLayout, mobileGridColumns: 1 | 2): string {
    if (tileLayout === 'vertical-event' && mobileGridColumns === 2) {
        // Mobile 32px / desktop 24px between rows; keep 12px column gutters.
        return 'gap-x-3 gap-y-8 lg:gap-y-6';
    }
    return 'gap-3';
}

/**
 * How many product tiles fill the widest CSS grid row. Keeping this independent of the viewport
 * makes the server and client render the interstitial in the same DOM position.
 */
export function resolveInterstitialAfterItemCount(tileLayout: ProductTileCustomLayout): number {
    return tileLayout === 'vertical-event' ? 4 : 3;
}

function resolveResponsiveImageDimensions(
    tileLayout: ProductTileCustomLayout,
    hasRefinementsPanel: boolean,
    mobileGridColumns: 1 | 2
): ProductGridImageDimensions {
    if (tileLayout === 'vertical-event' || (tileLayout === 'vertical' && mobileGridColumns === 2)) {
        return {
            widths: hasRefinementsPanel
                ? PRODUCT_GRID_EVENT_WIDTHS_WITH_REFINEMENTS
                : PRODUCT_GRID_EVENT_WIDTHS_WITHOUT_REFINEMENTS,
        };
    }
    if (tileLayout === 'vertical') {
        return {
            widths: hasRefinementsPanel
                ? PRODUCT_GRID_VERTICAL_WIDTHS_WITH_REFINEMENTS
                : PRODUCT_GRID_VERTICAL_WIDTHS_WITHOUT_REFINEMENTS,
        };
    }
    return {
        widths: PRODUCT_CARD_HORIZONTAL_WIDTHS,
        heights: PRODUCT_CARD_HORIZONTAL_HEIGHTS,
    };
}

function InterstitialSlot({ children }: { children: ReactNode }) {
    return (
        <div className="col-span-full row-start-2" data-testid="product-grid-interstitial">
            {children}
        </div>
    );
}

function NoProductsMessage({ criticalSize, nonCriticalSize }: { criticalSize: number; nonCriticalSize: number }) {
    const { t } = useTranslation('common');

    if (criticalSize > 0 || nonCriticalSize > 0) {
        return null;
    }
    return (
        <div className="col-span-full text-center py-12">
            <p className="text-lg text-muted-foreground">{t('noProductsFound')}</p>
        </div>
    );
}

function ProductTile({
    product,
    tileLayout,
    handleProductClick,
    basketControlsDisabled,
    isAddToCartDisabled,
    useHorizontalEventCard,
}: {
    product: ProductSearchHit;
    tileLayout: ProductTileCustomLayout;
    handleProductClick?: (product: ProductSearchHit) => void;
    basketControlsDisabled: boolean;
    isAddToCartDisabled?: (product: ProductSearchHit) => boolean;
    useHorizontalEventCard?: boolean;
}) {
    return (
        <ProductTileCustom
            layout={tileLayout}
            product={product}
            handleProductClick={handleProductClick}
            basketControlsDisabled={resolveTileBasketControlsDisabled(
                basketControlsDisabled,
                product,
                isAddToCartDisabled
            )}
            useHorizontalEventCard={useHorizontalEventCard}
        />
    );
}

function NonCriticalContent({
    nonCritical,
    criticalSize,
    imageDimensions,
    handleProductClick,
    tileLayout,
    basketControlsDisabled,
    isAddToCartDisabled,
    useHorizontalEventCard,
    interstitial,
    interstitialAfterCount,
}: {
    nonCritical: Promise<ProductSearchHit[]>;
    criticalSize: number;
    imageDimensions: ProductGridImageDimensions;
    handleProductClick?: (product: ProductSearchHit) => void;
    tileLayout: ProductTileCustomLayout;
    basketControlsDisabled: boolean;
    isAddToCartDisabled?: (product: ProductSearchHit) => boolean;
    useHorizontalEventCard?: boolean;
    interstitial?: ReactNode;
    /** Absolute product index after which to insert; only used when > criticalSize. */
    interstitialAfterCount: number;
}) {
    const products = use(nonCritical);
    const needsInterstitial = Boolean(interstitial) && interstitialAfterCount > criticalSize;
    const insertAfterLocalIndex = interstitialAfterCount - criticalSize - 1;
    const insertWithinList = needsInterstitial && insertAfterLocalIndex >= 0 && insertAfterLocalIndex < products.length;
    const insertAfterAll =
        needsInterstitial && products.length > 0 && interstitialAfterCount > criticalSize + products.length;

    return (
        <DynamicImageProvider value={imageDimensions}>
            {products.map((product, index) => (
                <Fragment key={product.productId}>
                    <ProductTile
                        product={product}
                        tileLayout={tileLayout}
                        handleProductClick={handleProductClick}
                        basketControlsDisabled={basketControlsDisabled}
                        isAddToCartDisabled={isAddToCartDisabled}
                        useHorizontalEventCard={useHorizontalEventCard}
                    />
                    {insertWithinList && index === insertAfterLocalIndex ? (
                        <InterstitialSlot>{interstitial}</InterstitialSlot>
                    ) : null}
                </Fragment>
            ))}
            {insertAfterAll ? <InterstitialSlot>{interstitial}</InterstitialSlot> : null}
            <NoProductsMessage criticalSize={criticalSize} nonCriticalSize={products.length} />
        </DynamicImageProvider>
    );
}

function ProductGridSkeleton({
    count = 8,
    tileLayout,
    mobileGridColumns = 1,
}: {
    count?: number;
    tileLayout: ProductTileCustomLayout;
    mobileGridColumns?: 1 | 2;
}) {
    return (
        <div
            className={cn(
                'grid min-w-0 items-stretch',
                resolveGridGapClassName(tileLayout, mobileGridColumns),
                resolveGridClassName(tileLayout, mobileGridColumns)
            )}>
            {Array.from({ length: count }, (_, index) => (
                <ProductTileCustomSkeleton key={`grid-skeleton-${index}`} layout={tileLayout} />
            ))}
        </div>
    );
}

/**
 * Outer `grid-global` column shell (col 2–11 on desktop). Skipped when the consumer already places the grid inside
 * its own `grid-global` column (e.g. wishlist pages), otherwise the columns would be nested twice and the grid
 * would shrink.
 */
function GridShell({
    withLayoutColumns,
    children,
    ...props
}: {
    withLayoutColumns: boolean;
    children: ReactNode;
} & Omit<ComponentProps<'div'>, 'className' | 'children'>): ReactElement {
    if (!withLayoutColumns) {
        return (
            <div className="min-w-0" {...props}>
                {children}
            </div>
        );
    }
    return (
        <div className="grid-global flex-1" {...props}>
            <div className="col-span-full min-w-0 lg:col-start-2 lg:col-span-10">{children}</div>
        </div>
    );
}

/**
 * ProductGrid wraps all tiles in a shared context provider to reduce hydration overhead. Instead of each tile
 * initializing its own hooks (navigate, config, translation, currency), the provider initializes them once and shares
 * them via context.
 *
 * Performance optimizations: The grid accepts both synchronous (critical) and asynchronous (non-critical) data as
 * input. Depending on the positioning of the grid in the DOM, this allows the consumer to influence metrics such as
 * LCP. The images associated with product items that have been marked as critical are also loaded with high priority
 * and eagerly. Non-critical tiles are deferred until an idle frame is available, reducing initial render blocking time
 * (TBT) and improving LCP by prioritizing the first batch of visible tiles.
 */
export default function ProductGrid({
    critical,
    nonCritical,
    nonCriticalCount = 0,
    maxSkeletonCount,
    hasRefinementsPanel = true,
    handleProductClick,
    isLoading = false,
    /** Eataly PLP default: horizontal (carousel) tiles; set `vertical` for square Figma PLP. */
    tileLayout = 'horizontal',
    mobileGridColumns = 1,
    isAddToCartDisabled,
    /** Wishlist: render event products with the horizontal card (same as courses). */
    useHorizontalEventCard = false,
    /** Optional full-width slot inserted after the first product row (e.g. Page Designer grid promo). */
    interstitial,
    /** Set `false` when the parent already provides the `grid-global` column shell (e.g. wishlist). */
    withLayoutColumns = true,
}: {
    critical?: ProductSearchHit[];
    nonCritical?: Promise<ProductSearchHit[]>;
    nonCriticalCount?: number;
    /** Preferred full-grid loading floor (viewport-aware). Falls back by mobile columns. */
    maxSkeletonCount?: number;
    hasRefinementsPanel?: boolean;
    handleProductClick?: (product: ProductSearchHit) => void;
    isLoading?: boolean;
    tileLayout?: ProductTileCustomLayout;
    mobileGridColumns?: 1 | 2;
    isAddToCartDisabled?: (product: ProductSearchHit) => boolean;
    useHorizontalEventCard?: boolean;
    interstitial?: ReactNode;
    withLayoutColumns?: boolean;
}): ReactElement {
    // Triggers getBasket so tiles can match `productItems` to search hits (see useProductTileBasketLine).
    useBasket({ autoLoad: true });
    const basketLineReady = useBasketLineReadiness();
    const [basketControlsDisabled, setBasketControlsDisabled] = useState(true);
    useEffect(() => {
        let alive = true;
        void basketLineReady.then(() => {
            if (alive) setBasketControlsDisabled(false);
        });
        return () => {
            alive = false;
        };
    }, [basketLineReady]);

    const criticalData = critical ?? [];
    const l = criticalData.length;
    const interstitialAfterCount = resolveInterstitialAfterItemCount(tileLayout);
    // Skip interstitial while loading so empty promos do not reserve a row of skeletons.
    const activeInterstitial = !isLoading && interstitial ? interstitial : undefined;
    const insertInCritical = Boolean(activeInterstitial) && interstitialAfterCount <= l;

    const imageDimensions = useMemo(
        () => resolveResponsiveImageDimensions(tileLayout, hasRefinementsPanel, mobileGridColumns),
        [tileLayout, hasRefinementsPanel, mobileGridColumns]
    );

    // Initialize the `<DynamicImageProvider/>` behavior for the scope of this grid.
    // Out-of-the-box we make sure that the product images of all products considered critical (displayed inside a
    // `<DynamicImage/>` component) should be loaded eagerly with high priority.
    const hasSource = useCallback(() => true, []);
    const loadingSkeletonCount = Math.max(
        criticalData.length + nonCriticalCount,
        maxSkeletonCount ?? (mobileGridColumns === 2 ? 4 : 6)
    );

    // Defer rendering of non-critical tiles until an idle frame is available.
    // This improves initial render performance by prioritizing the critical (above-the-fold) tiles.
    const shouldRenderNonCritical = useDeferredRender(nonCriticalCount > 0);

    const gridClassName = cn(
        'min-w-0 grid items-stretch',
        resolveGridGapClassName(tileLayout, mobileGridColumns),
        resolveGridClassName(tileLayout, mobileGridColumns)
    );

    if (isLoading) {
        return (
            <ProductTileProvider>
                <GridShell
                    withLayoutColumns={withLayoutColumns}
                    data-testid="product-grid-loading-state"
                    aria-busy="true">
                    <ProductGridSkeleton
                        count={loadingSkeletonCount}
                        tileLayout={tileLayout}
                        mobileGridColumns={mobileGridColumns}
                    />
                </GridShell>
            </ProductTileProvider>
        );
    }

    return (
        <ProductTileProvider>
            <GridShell withLayoutColumns={withLayoutColumns}>
                <div className={gridClassName}>
                    {l > 0 && (
                        <DynamicImageProvider value={{ hasSource, ...imageDimensions }}>
                            {criticalData.map((product, index) => (
                                <Fragment key={product.productId}>
                                    <ProductTile
                                        product={product}
                                        tileLayout={tileLayout}
                                        handleProductClick={handleProductClick}
                                        basketControlsDisabled={basketControlsDisabled}
                                        isAddToCartDisabled={isAddToCartDisabled}
                                        useHorizontalEventCard={useHorizontalEventCard}
                                    />
                                    {insertInCritical && index === interstitialAfterCount - 1 ? (
                                        <InterstitialSlot>{activeInterstitial}</InterstitialSlot>
                                    ) : null}
                                </Fragment>
                            ))}
                        </DynamicImageProvider>
                    )}
                    {/*
                    Three-phase rendering strategy for non-critical content:

                    Phase 1 (Pre-Idle): nonCritical exists BUT shouldRenderNonCritical is false
                    - We have a promise with non-critical products, but idle callback hasn't triggered yet
                    - Show simple skeletons without mounting the Suspense boundary (until requestIdleCallback fires)
                    - Why: Mounting Suspense immediately would force React to process it during initial render,
                      blocking the main thread (causing TBT, delaying LCP). By deferring the Suspense boundary
                      itself, we keep the initial render minimal (just critical tiles + lightweight skeletons).

                    Phase 2 (Post-Idle, Pending): shouldRenderNonCritical is true, promise is pending
                    - Idle callback has triggered, Suspense boundary is now mounted
                    - Show skeletons via Suspense fallback while the promise resolves

                    Phase 3 (Resolved): shouldRenderNonCritical is true, promise resolved
                    - All non-critical tiles are rendered
                    - Skeletons are replaced with actual product tiles
                */}
                    {nonCritical && shouldRenderNonCritical ? (
                        // Phase 2 & 3: Post-idle — mount Suspense boundary and render tiles
                        <Suspense
                            fallback={Array.from({ length: nonCriticalCount }, (_, i) => (
                                <ProductTileCustomSkeleton key={i} layout={tileLayout} />
                            ))}>
                            <NonCriticalContent
                                nonCritical={nonCritical}
                                criticalSize={l}
                                imageDimensions={imageDimensions}
                                handleProductClick={handleProductClick}
                                tileLayout={tileLayout}
                                basketControlsDisabled={basketControlsDisabled}
                                isAddToCartDisabled={isAddToCartDisabled}
                                useHorizontalEventCard={useHorizontalEventCard}
                                interstitial={insertInCritical ? undefined : activeInterstitial}
                                interstitialAfterCount={interstitialAfterCount}
                            />
                        </Suspense>
                    ) : nonCritical ? (
                        // Phase 1: Pre-idle — show lightweight skeletons before Suspense boundary mounts
                        // This prevents React from processing the Suspense boundary during initial render,
                        // reducing Total Blocking Time (TBT) and improving LCP for critical tiles.
                        Array.from({ length: nonCriticalCount }, (_, i) => (
                            <ProductTileCustomSkeleton key={i} layout={tileLayout} />
                        ))
                    ) : (
                        // No non-critical promise exists — show empty state if no critical tiles either.
                        // If the insert index is past critical (fewer hits than a full first row), still
                        // place the promo after all critical tiles.
                        <>
                            <NoProductsMessage criticalSize={l} nonCriticalSize={0} />
                            {!insertInCritical && activeInterstitial && l > 0 && interstitialAfterCount > l ? (
                                <InterstitialSlot>{activeInterstitial}</InterstitialSlot>
                            ) : null}
                        </>
                    )}
                </div>
            </GridShell>
        </ProductTileProvider>
    );
}
